import {
  createContext,
  createSignal,
  For,
  onCleanup,
  Show,
  useContext,
  type ParentComponent,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import { topLayerMount } from './floatingLayer';
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-solid';
import { haptic } from '~/shared/lib/haptics';

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  durationMs?: number;
  action?: ToastAction;
  undo?: () => void;
}

export interface ToastItem extends ToastOptions {
  id: number;
  kind: ToastKind;
  message: string;
  createdAt: number;
}

interface ToastContextValue {
  show: (kind: ToastKind, message: string, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
  success: (message: string, opts?: ToastOptions) => number;
  error: (message: string, opts?: ToastOptions) => number;
  info: (message: string, opts?: ToastOptions) => number;
  warning: (message: string, opts?: ToastOptions) => number;
}

const ToastContext = createContext<ToastContextValue>();

const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 3500,
  info: 4000,
  warning: 5000,
  error: 6000,
};

let nextId = 0;

export const ToastProvider: ParentComponent = (props) => {
  const [items, setItems] = createSignal<ToastItem[]>([]);
  const timers = new Map<number, ReturnType<typeof setTimeout>>();

  const dismiss: ToastContextValue['dismiss'] = (id) => {
    const t = timers.get(id);
    if (t !== undefined) {
      clearTimeout(t);
      timers.delete(id);
    }
    setItems((list) => list.filter((t) => t.id !== id));
  };

  const show: ToastContextValue['show'] = (kind, message, opts = {}) => {
    const id = ++nextId;
    const duration = opts.durationMs ?? DEFAULT_DURATION[kind];
    const item: ToastItem = {
      id,
      kind,
      message,
      createdAt: Date.now(),
      durationMs: duration,
      ...(opts.action ? { action: opts.action } : {}),
      ...(opts.undo ? { undo: opts.undo } : {}),
    };
    setItems((list) => [...list.slice(-4), item]);
    if (kind === 'error') haptic('error');
    else if (kind === 'warning') haptic('warning');
    else if (kind === 'success') haptic('success');
    if (duration > 0) {
      timers.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  };

  onCleanup(() => {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
  });

  const api: ToastContextValue = {
    show,
    dismiss,
    success: (m, o) => show('success', m, o),
    error: (m, o) => show('error', m, o),
    info: (m, o) => show('info', m, o),
    warning: (m, o) => show('warning', m, o),
  };

  return (
    <ToastContext.Provider value={api}>
      {props.children}
      <ToastRegion items={items()} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};

import type { JSX } from 'solid-js';

const ICONS: Record<ToastKind, (props: { class: string }) => JSX.Element> = {
  success: (p) => <CheckCircle2 class={p.class} />,
  error: (p) => <XCircle class={p.class} />,
  info: (p) => <Info class={p.class} />,
  warning: (p) => <TriangleAlert class={p.class} />,
};

const KIND_COLORS: Record<ToastKind, string> = {
  success: 'var(--color-success)',
  error: 'var(--color-danger)',
  info: 'var(--color-accent)',
  warning: 'var(--color-warning)',
};

const ToastRegion = (props: { items: ToastItem[]; onDismiss: (id: number) => void }) => (
  <Show when={topLayerMount()} keyed>
    {(mount) => (
      <Portal mount={mount}>
        <div
          role="region"
          aria-label="Уведомления"
          class="fixed top-4 right-4 z-[1500] flex flex-col items-end gap-2 pointer-events-none"
        >
          <For each={props.items}>
            {(item) => <ToastCard item={item} onDismiss={props.onDismiss} />}
          </For>
        </div>
      </Portal>
    )}
  </Show>
);

const ToastCard = (props: { item: ToastItem; onDismiss: (id: number) => void }) => {
  const Icon = ICONS[props.item.kind];
  return (
    <div
      role={props.item.kind === 'error' ? 'alert' : 'status'}
      aria-live={props.item.kind === 'error' ? 'assertive' : 'polite'}
      class="glass-strong pointer-events-auto min-w-[320px] max-w-[420px] p-3 flex items-start gap-3 animate-toast-in"
      style={{
        'border-left': `3px solid ${KIND_COLORS[props.item.kind]}`,
      }}
    >
      <span style={{ color: KIND_COLORS[props.item.kind] }} class="mt-0.5">
        <Icon class="h-5 w-5" />
      </span>
      <p class="flex-1 text-[14px] leading-snug text-primary">
        {props.item.message}
      </p>
      <Show when={props.item.action}>
        {(action) => (
          <button
            type="button"
            class="press text-[13px] font-medium text-accent hover:underline"
            onClick={() => {
              action().onClick();
              props.onDismiss(props.item.id);
            }}
          >
            {action().label}
          </button>
        )}
      </Show>
      <Show when={props.item.undo}>
        {(undo) => (
          <button
            type="button"
            class="press text-[13px] font-medium text-accent hover:underline"
            onClick={() => {
              undo()();
              props.onDismiss(props.item.id);
            }}
          >
            Отменить
          </button>
        )}
      </Show>
      <button
        type="button"
        class="press p-1 text-tertiary hover:text-primary"
        onClick={() => props.onDismiss(props.item.id)}
        aria-label="Закрыть уведомление"
      >
        <X class="h-4 w-4" />
      </button>

      <style>{`
        @keyframes toast-in {
          from { transform: translateY(-8px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .animate-toast-in { animation: toast-in var(--duration-base) var(--ease-ios) both; }
      `}</style>
    </div>
  );
};
